import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import moment from 'moment'
import toast from 'react-hot-toast'
import { fetchData } from '../../../helpers/fetchData'
import CustomTable from '../../../shared/CustomTable'
import DatePicker from '../../../shared/DatePicker'
import DropdownField from '../../../shared/DropdownField'
import CustomButton from '../../../shared/CustomButton'
import PageHeader from '../../../shared/PageHeader'
import { formatDateTime } from '../../../utils/FormateDateTime'
import { MdWarning } from 'react-icons/md'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'
import { AmiriRegBase64 } from '../../../assets/fonts/Amiri'
import { AmiriBoldBase64 } from '../../../assets/fonts/AmiriB'
import { payersOptionsFormat } from '../../../constants/BeneficiaryConstants'
import { validateOneMonthRange, getMinDate, getOneMonthMaxDate } from '../../../utils/dateRangeValidation'

const REPORT_NAME = 'Not Aproved Results'
const REPORT_FILE_SLUG = 'not_aproved_results'

const NotApprovedAuditsReport = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const auth = useSelector(state => state.auth)
  const [activeTab, setActiveTab] = useState('lab') // 'lab' or 'radiology'
  const [loading, setLoading] = useState(false)
  const [fieldLoading, setFieldLoading] = useState(false)
  const [data, setData] = useState(null)
  const [payerList, setPayerList] = useState([])
  const [exporting, setExporting] = useState(false)
  const [filters, setFilters] = useState({
    start_date: moment().subtract(1, 'month').format('YYYY-MM-DD'),
    end_date: moment().format('YYYY-MM-DD'),
    payer_id: ''
  })

  const getPayersList = async () => {
    setFieldLoading(true)
    const response = await fetchData('POST', '/v3/payer/get', {})
    if (response?.data) {
      setPayerList(response.data)
    }
    setFieldLoading(false)
  }

  const fetchNotApprovedAudits = async (
    category = activeTab,
    overrideFilters = null
  ) => {
    setLoading(true)
    try {
      const activeFilters = overrideFilters || filters
      const payload = {
        start_date: activeFilters.start_date,
        end_date: activeFilters.end_date,
        category: category
      }
      if (activeFilters.payer_id) payload.payer_id = activeFilters.payer_id

      const response = await fetchData(
        'POST',
        '/v3/invoices/audit-results/not-approved-results',
        payload
      )

      if (response) {
        setData(response)
      }
    } catch (error) {
      console.error('Error fetching not approved audits:', error)
      toast.error(t('Failed to fetch not approved audits'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchNotApprovedAudits()
  }, [activeTab])

  useEffect(() => {
    getPayersList()
  }, [])

  const handleDateChange = event => {
    const { name, value } = event.target || {}
    setFilters(prev => ({
      ...prev,
      [name]: moment(value).format('YYYY-MM-DD')
    }))
  }

  const handleApplyFilters = () => {
    if (!validateOneMonthRange(filters.start_date, filters.end_date, t)) return
    fetchNotApprovedAudits(activeTab, filters)
  }

  const handleResetFilters = () => {
    const initialFilters = {
      start_date: moment().subtract(1, 'month').format('YYYY-MM-DD'),
      end_date: moment().format('YYYY-MM-DD'),
      payer_id: ''
    }
    setFilters(initialFilters)
    fetchNotApprovedAudits(activeTab, initialFilters)
  }

  const handleTabChange = tab => {
    setActiveTab(tab)
  }

  const handleExport = async format => {
    if (!data?.data || data.data.length === 0) {
      toast.error(t('No data to export'))
      return
    }

    setExporting(true)
    try {
      if (format === 'excel') {
        exportToExcel()
      } else if (format === 'pdf') {
        exportToPDF()
      }
    } catch (error) {
      console.error('Error exporting report:', error)
    } finally {
      setExporting(false)
    }
  }

  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(
      data.data.map(row => ({
        [t('Visit Date')]: formatDateTime(row.visit_date),
        [t('Patient Name')]: row.patient_name || '-',
        [t('MRN')]: row.file_number || '-',
        [t('Document ID')]: row.document_id || '-',
        [t('Visit ID')]: String(row.visit_id || '-'),
        [t('Payer')]: row.payer_name || '-',
        [t('Item Name')]: row.item_name || '-',
        [t('Item Code')]: row.item_code || '-',
        [t('Description')]: row.description || row.descrption || '-'
      }))
    )
    ws['!cols'] = [
      { wch: 26 }, // Visit Date
      { wch: 26 }, // Patient Name
      { wch: 16 }, // File Number
      { wch: 16 }, // Document ID
      { wch: 14 }, // Visit ID
      { wch: 24 }, // Payer
      { wch: 28 }, // Item Name
      { wch: 16 }, // Item Code
      { wch: 48 } // Description
    ]
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, REPORT_NAME)
    XLSX.writeFile(
      wb,
      `${REPORT_FILE_SLUG}_${activeTab}_${filters.start_date}_${filters.end_date}.xlsx`
    )
  }

  const exportToPDF = () => {
    const doc = new jsPDF('landscape')
    doc.addFileToVFS('Amiri-Regular.ttf', AmiriRegBase64)
    doc.addFont('Amiri-Regular.ttf', 'Amiri', 'normal')
    doc.addFileToVFS('Amiri-Bold.ttf', AmiriBoldBase64)
    doc.addFont('Amiri-Bold.ttf', 'Amiri', 'bold')
    doc.setFont('Amiri', 'normal')

    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()
    const margin = 5
    const usable = pageWidth - margin * 2
    const currentDate = moment().format('DD/MM/YYYY HH:mm')
    const startDate = moment(filters.start_date).format('DD/MM/YYYY')
    const endDate = moment(filters.end_date).format('DD/MM/YYYY')
    const orgName =
      auth?.buisnessInfo?.name_en ||
      auth?.buisnessInfo?.name_ar ||
      t('Organization')
    const location = auth?.buisnessInfo?.address || ''

    const drawHeader = () => {
      doc.setFillColor(26, 54, 93)
      doc.rect(0, 0, pageWidth, 24, 'F')
      if (auth?.buisnessInfo?.logo) {
        try {
          doc.addImage(auth.buisnessInfo.logo, 'PNG', margin, 2, 16, 16)
        } catch (_) {}
      }
      doc.setTextColor(255, 255, 255)
      doc.setFontSize(10)
      doc.setFont('Amiri', 'bold')
      doc.text(orgName, margin + 20, 9)
      doc.setFontSize(7)
      doc.setFont('Amiri', 'normal')
      if (location) doc.text(location, margin + 20, 15)
      doc.setFontSize(11)
      doc.setFont('Amiri', 'bold')
      doc.text(REPORT_NAME, pageWidth - margin, 9, { align: 'right' })
      doc.setFontSize(7)
      doc.setFont('Amiri', 'normal')
      doc.text(`${t('Printed On')}: ${currentDate}`, pageWidth - margin, 15, {
        align: 'right'
      })
    }

    drawHeader()
    let y = 27
    doc.setTextColor(50, 50, 50)
    doc.setFontSize(7)
    doc.setFont('Amiri', 'normal')
    doc.text(`${t('Period')}: ${startDate} - ${endDate}`, margin, y)
    doc.text(
      `${t('Category')}: ${
        activeTab === 'lab' ? t('Laboratory') : t('Radiology')
      }`,
      pageWidth - margin,
      y,
      { align: 'right' }
    )
    doc.setDrawColor(200, 200, 200)
    doc.setLineWidth(0.3)
    doc.line(margin, y + 2.5, pageWidth - margin, y + 2.5)
    y += 6

    const tableData = data.data.map(row => [
      formatDateTime(row.visit_date),
      row.patient_name || '-',
      row.file_number || '-',
      row.document_id || '-',
      String(row.visit_id || '-'),
      row.payer_name || '-',
      row.item_name || '-',
      row.item_code || '-',
      row.description || row.descrption || '-'
    ])

    autoTable(doc, {
      startY: y,
      head: [
        [
          t('Visit Date'),
          t('Patient Name'),
          t('MRN'),
          t('Document ID'),
          t('Visit ID'),
          t('Payer'),
          t('Item Name'),
          t('Item Code'),
          t('Description')
        ]
      ],
      body: tableData,
      theme: 'grid',
      margin: { left: margin, right: margin, top: 28 },
      tableWidth: usable,
      styles: {
        fontSize: 7,
        cellPadding: { top: 1.8, right: 1.5, bottom: 1.8, left: 1.5 },
        lineColor: [180, 180, 180],
        lineWidth: 0.2,
        font: 'Amiri',
        overflow: 'linebreak',
        textColor: [20, 20, 20],
        minCellHeight: 6
      },
      headStyles: {
        fillColor: [26, 54, 93],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'center',
        font: 'Amiri',
        fontSize: 7,
        valign: 'middle',
        minCellHeight: 6
      },
      alternateRowStyles: {
        fillColor: [248, 249, 252]
      },
      bodyStyles: {
        fillColor: [255, 255, 255],
        font: 'Amiri',
        fontSize: 7,
        fontStyle: 'normal',
        minCellHeight: 6
      },
      showHead: 'everyPage',
      columnStyles: {
        0: { cellWidth: 30 },
        1: { cellWidth: 28 },
        2: { cellWidth: 18 },
        3: { cellWidth: 20 },
        4: { cellWidth: 16 },
        5: { cellWidth: 26 },
        6: { cellWidth: 30 },
        7: { cellWidth: 18 },
        8: { cellWidth: 'auto' }
      },
      didDrawPage: () => {
        drawHeader()
        const pageCount = doc.internal.getNumberOfPages()
        doc.setFontSize(7)
        doc.setTextColor(130, 130, 130)
        doc.text(
          `${t('Page')} ${doc.internal.getCurrentPageInfo().pageNumber} ${t(
            'of'
          )} ${pageCount}`,
          pageWidth / 2,
          pageHeight - 4,
          { align: 'center' }
        )
      }
    })

    doc.save(
      `${REPORT_FILE_SLUG}_${activeTab}_${filters.start_date}_${filters.end_date}.pdf`
    )
  }

  const columns = [
    {
      header: t('Visit Date'),
      body: row => formatDateTime(row.visit_date)
    },
    {
      header: t('Patient Name'),
      body: row => row.patient_name
    },
    {
      header: t('MRN'),
      body: row => row.file_number
    },
    {
      header: t('Document ID'),
      body: row => row.document_id
    },
    {
      header: t('Visit ID'),
      body: row => row.visit_id
    },
    {
      header: t('Payer'),
      body: row => row.payer_name
    },
    {
      header: t('Item Name'),
      body: row => row.item_name
    },
    {
      header: t('Item Code'),
      body: row => row.item_code
    }
  ]

  return (
    <div className='main-page-container p-4'>
      <PageHeader
        title={t(REPORT_NAME)}
        Icon={i18n.language == 'en' ? FaArrowLeft : FaArrowRight}
        onIconClick={() => {
          navigate(-1)
        }}
      />

      <div className='flex gap-2 mb-4 border-b border-gray-200'>
        <button
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'lab'
              ? 'text-blue border-b-2 border-blue'
              : 'text-gray-600 hover:text-gray-800'
          }`}
          onClick={() => handleTabChange('lab')}
        >
          {t('Laboratory')}
        </button>
        <button
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'radiology'
              ? 'text-blue border-b-2 border-blue'
              : 'text-gray-600 hover:text-gray-800'
          }`}
          onClick={() => handleTabChange('radiology')}
        >
          {t('Radiology')}
        </button>
      </div>

      {/* Filters */}
      <div className='flex flex-wrap gap-2 mb-4'>
        <div className='w-[200px]'>
          <DatePicker
            name='start_date'
            value={
              filters.start_date
                ? moment(filters.start_date, 'YYYY-MM-DD').toDate()
                : ''
            }
            onChange={handleDateChange}
            useFormik={false}
            placeholder={t('StartDate')}
          />
        </div>
        <div className='w-[200px]'>
          <DatePicker
            name='end_date'
            value={
              filters.end_date
                ? moment(filters.end_date, 'YYYY-MM-DD').toDate()
                : ''
            }
            onChange={handleDateChange}
            useFormik={false}
            placeholder={t('EndDate')}
            minDate={getMinDate(filters.start_date)}
            maxDate={getOneMonthMaxDate(filters.start_date)}
          />
        </div>
        <div className='w-[220px]'>
          <DropdownField
            useFormik={false}
            value={filters.payer_id}
            handleChange={e =>
              setFilters(prev => ({ ...prev, payer_id: e.target?.value || '' }))
            }
            options={payersOptionsFormat(payerList)}
            placeholder={t('Select Payer')}
            loading={fieldLoading}
            filter
          />
        </div>
        <div className='flex gap-2 items-start flex-wrap'>
          <CustomButton
            title={t('ApplyFilters')}
            buttonType='primary'
            icon='pi pi-filter'
            className='w-fit h-[35px]'
            disabled={loading}
            onClick={handleApplyFilters}
          />
          <CustomButton
            title={t('Reset')}
            buttonType='reset'
            icon='pi pi-sync'
            disabled={loading}
            className='w-fit h-[35px]'
            onClick={handleResetFilters}
          />
          <CustomButton
            title={t('Export Excel')}
            buttonType='success'
            icon='pi pi-file-excel'
            className='w-fit h-[35px]'
            disabled={loading || exporting || !data?.data?.length}
            loading={exporting}
            onClick={() => handleExport('excel')}
          />
          <CustomButton
            title={t('Export PDF')}
            buttonType='danger'
            icon='pi pi-file-pdf'
            className='w-fit h-[35px]'
            disabled={loading || exporting || !data?.data?.length}
            loading={exporting}
            onClick={() => handleExport('pdf')}
          />
        </div>
      </div>

      {/* Table */}
      <CustomTable
        data={data?.data || []}
        isLoading={loading}
        columns={columns}
        emptyMessage={t('No not approved audits found')}
      />
    </div>
  )
}

export default NotApprovedAuditsReport
